import { asArray } from "@foretagsplatsen/core-helpers/array";
import { defaultTag, ensureDefaultOptions, rootTagRenderer } from "./base.js";
import { isObjectLiteral } from "../../helpers/object-util.js";

/**
 * Define a new element that can not contain a child element, like an icon or
 * an image.
 *
 * It parses the all function arguments to extract the argument to pass to the
 * `fn` function. The extracted argument is:
 *   - {object} options - The options passed to the element.
 *
 * This makes it compatible with HTMLCanvas.
 *
 * Since `class` is a JS keyword, it can't really be used everywhere. It can be
 * used as an object key, but not as a variable name.
 *
 * ```js
 * let foo = { class: 42 }; // Valid
 * let { class } = computeClass(); // Invalid
 * ```
 *
 * When invoked, the `fn` function must return a literal object of the
 * following form:
 *  - tag: the name of a tagBrush that will be used to render
 *      the top level DOMElement of the element
 *  - options: options to pass to the top level DOMElement of the element
 *
 * This allows to intercept the creation of the root element, and use the `as`
 * parameter if provided.
 * It will also make sure that the `class` parameter is injected as well as the
 * element-specific class name.
 *
 * @param {string} name - Name of the element to define. This string will also
 *   be used to generate a CSS class specific to this element
 * @param {function} fn - Function invoke to render the element. This method is
 *   invoked with some options as a literal object
 * @return {Renderable}
 */
export default function defineElementWithNoChild(name, fn) {
	return (htmlOrOptions) =>
		elementWithNoChildRenderer({ htmlOrOptions, name, fn });
}

function elementWithNoChildRenderer({ htmlOrOptions, name, fn }) {
	const optionsAreProvided = isObjectLiteral(htmlOrOptions);

	const options = optionsAreProvided ? htmlOrOptions : {};

	const {
		tag = defaultTag,
		options: rootOptions,
		children: rootChildren = [],
		ref: rootRef,
	} = fn(options);

	ensureDefaultOptions({ name, options, rootOptions });

	if (optionsAreProvided) {
		const root = (html) =>
			rootTagRenderer({
				as: options.as,
				html,
				tag,
				ref: options.ref,
				rootRef,
				options: rootOptions,
				children: asArray(rootChildren),
			});

		options.renderOn?.render(root);
		return root;
	}

	rootTagRenderer({
		as: options.as,
		html: htmlOrOptions,
		tag,
		ref: options.ref,
		options: rootOptions,
		children: asArray(rootChildren),
	});

	return undefined;
}
